import {
  Box,
  Button,
  Center,
  Container,
  Flex,
  Heading,
  ListItem,
  SimpleGrid,
  Stack,
  Text,
  UnorderedList,
} from '@chakra-ui/react'
import type { ReactElement } from 'react'

import { makeServerProps } from '@/server/query'

import makePage from '@/client/services/pages/makePage'
import { faq, getExploreUrl } from '@/client/services/url'

import AuthLink from '@/components/Auth/AuthLink'
import FAQ from '@/components/FAQ/FAQ'
import WavyPath from '@/components/Icons/WavyPath'
import Layout from '@/components/Layout/Layout'
import PublicImage from '@/components/PublicImage/PublicImage'
import ShwLink from '@/components/ShwLink'

const benefits = [
  {
    title: 'Build your confidence',
    description:
      'Unlike a traditional classroom environment where you might be one of many students in a room, ' +
      'peer to peer tutoring can offer a more relaxed, approachable space to find your voice, ask questions and build confidence.',
  },
  {
    title: 'Foster connections',
    description:
      'A peer tutor is not just a tutor - they can be a friend, collaborator, mentor, role model and more. ' +
      'Getting tutored by a peer who can relate to what you’re going through can offer the right motivation and accountability to reach your goals.',
  },
  {
    title: 'Reinforce your learning',
    description:
      'The peer tutoring benefits are not just limited to the tutee. Being a peer tutor yourself is a fantastic way to boost your own learning journey; ' +
      'research shows that teaching what you know to others is a strong way to internalize and apply what you’ve learned!',
  },
]

const FAQ_ITEMS = [
  {
    question: 'What is meant by “peer tutoring”?',
    answer:
      'Peer tutoring is when students tutor other students, either in one-to-one or small-group ' +
      'settings. At Schoolhouse, we currently enable small-group tutoring between learners and ' +
      'tutors.',
  },
  {
    question: 'What is the purpose of peer tutoring?',
    answer: () => (
      <Text>
        Learning doesn’t only happen between an instructor and a student! Peer tutoring benefits
        both tutees and tutors (
        <ShwLink href="https://journals.sagepub.com/doi/abs/10.3102/0034654307309920">
          Source
        </ShwLink>
        ). Peer-to-peer on Schoolhouse can be a strong complement to traditional classroom learning,
        offering opportunities to practice skills and reinforce concepts that were confusing as many
        times as a learner needs - all in a welcoming, small-group environment.
      </Text>
    ),
  },
  {
    question: 'What are three forms of peer tutoring?',
    answer: () => (
      <Text>
        Three formal examples include:
        <UnorderedList my="16px">
          <ListItem>
            Cross-age tutoring: in which an older learner tutors younger tutee(s).
          </ListItem>
          <ListItem>
            Reciprocal tutoring: where two or more students alternate between acting as the tutor
            and tutee in each session.
          </ListItem>
          <ListItem>
            Same-age tutoring: where peers within one or two years of age are paired to review key
            concepts, and may also alternate tutor and tutee roles (
            <ShwLink href="https://council-for-learning-disabilities.org/peer-tutoring-flexible-peer-mediated-strategy-that-involves-students-serving-as-academic-tutors/">
              Source
            </ShwLink>
            ).
          </ListItem>
        </UnorderedList>
        With a wide range of tutors from all different backgrounds on Schoolhouse, we experiment
        with a wide variety of different peer tutoring styles that may borrow elements from these
        three forms. We celebrate the diversity of our tutors, who range from 13-year-olds to
        retired college professors! We also embrace being life-long learners; many of our tutors
        also join sessions to learn, and our goal is to help learners build the confidence to be
        tutors themselves.
      </Text>
    ),
  },
  {
    question: 'Who would benefit from peer tutoring?',
    answer:
      'Our tutor community comes from all walks of life, so a peer can be found in anyone! We ' +
      'specifically specialize in supporting English-speaking students at the middle and high ' +
      'school levels, however, we welcome learners of any and all backgrounds.',
  },
  {
    question: 'Is peer tutoring effective?',
    answer: () => (
      <Text>
        Peer tutoring approaches are shown to have a positive impact on learning, with studies
        identifying benefits for both learners and tutors. Peer tutoring is also shown to be{' '}
        <ShwLink href="https://evidenceforlearning.org.au/the-toolkits/the-teaching-and-learning-toolkit/all-approaches/peer-tutoring/">
          more effective
        </ShwLink>{' '}
        when tutoring sessions are used to enhance or supplement normal classroom learning. At
        Schoolhouse, many of our sessions are designed to be a space for reinforcing and practicing
        what you learn in school.
      </Text>
    ),
  },
  {
    question: 'What are pros and cons of peer tutoring?',
    answer:
      'The pros: Getting a chance to learn from someone who can relate to you, who can offer ' +
      'different perspectives, and can help motivate you to learn and grow. The cons: Everyone ' +
      'learns (and teaches!) differently, so it may take extra time exploring to find a style ' +
      'that really clicks for you.',
  },
  {
    question: 'How do I become a peer tutor?',
    answer:
      'On Schoolhouse, you can tutor your peers by first joining our website and becoming ' +
      'certified in one or more topics we offer. After getting your certification approved by ' +
      'our team of volunteers and getting trained on safety and tutoring best practices, you ' +
      'can start hosting your own peer tutoring sessions!',
  },
  {
    question: 'How can I get free peer tutoring?',
    answer:
      'Sign up for an account on Schoolhouse and register for any available session to join a ' +
      'group of peer learners over Zoom!',
  },
]

function PeerTutoringIndex(): ReactElement {
  return (
    <Layout
      title="Peer Tutoring Benefits &amp; Summary"
      meta={{ title: 'Peer Tutoring Benefits & Summary' }}
    >
      <Container centerContent>
        <Box margin="auto" maxWidth="750px">
          <Flex direction="column" align="center" textAlign="center">
            <Text as="h1" variant="eyebrow" mb="16px">
              Peer Tutoring Benefits
            </Text>
            <Heading as="h2" size="3xl">
              Get Free Tutoring from Students Like You
            </Heading>
            <Text as="h3" fontSize="20px" my="32px" fontWeight="light" maxWidth="500px">
              Find help and reach your goals in a global community for virtual, peer-to-peer
              tutoring.
            </Text>
            <Flex mb="16px" align="center">
              <AuthLink mode="signup" returnTo={getExploreUrl()}>
                <Button variant="accent" width={['100%', 'auto']}>
                  Get Started
                </Button>
              </AuthLink>
            </Flex>
            <PublicImage maxWidth="100%" src="/home/hero-v2.png" />
          </Flex>
          <Heading as="h2" size="2xl" mt="32px" mb="16px">
            What is peer tutoring?
          </Heading>
          <p>
            Peer tutoring means getting help from other students, like you. Peer tutoring takes many
            forms: an older student tutoring a younger student in a subject they took years ago, a
            student of a higher ability tutoring another student who may be struggling, or students
            in the same class coming together to review material, with one student leading.
          </p>
          <p>
            Peer tutoring can also be one-to-one or in small groups! Both formats complement a
            traditional school classroom and can offer a safe, supportive environment to get the
            help you need.
          </p>
          <Heading as="h2" size="2xl" mt="48px" mb="80px">
            What are the benefits of peer tutoring?
          </Heading>
          <Stack align="center" direction="column" spacing="48px" px="16px">
            {benefits.map(({ title, description }, index) => (
              <Box maxWidth="500px" position="relative" key={title}>
                <Heading
                  position="absolute"
                  as="div"
                  fontSize="80px"
                  color="yellow.emphasized"
                  fontWeight={900}
                  top="-30px"
                  left="-20px"
                  zIndex={-1}
                >
                  {index + 1}
                </Heading>
                <Heading size="lg" as="h4" mb="16px">
                  {title}
                </Heading>
                <Text>{description}</Text>
              </Box>
            ))}
          </Stack>
          <Heading as="h2" size="2xl" mt="64px" mb="32px">
            What is peer tutoring on Schoolhouse.world?
          </Heading>
          <Text mb="64px">
            At Schoolhouse.world, getting peer to peer tutoring means going beyond the walls of your
            classroom and joining a global network of learners and tutors who have your back.
          </Text>
          <SimpleGrid columns={[1, 2]} spacing="32px" margin="auto" justifyItems="center" mb="64px">
            <Center>
              <PublicImage width="240px" src="/peer-tutoring/icon10.png" />
            </Center>
            <Center>
              <Box>
                <Heading size="xl" as="h3" mb="16px">
                  Find peer tutoring sessions
                </Heading>
                <Text>
                  On our platform, we offer peer tutoring in small groups - as a learner, you can
                  join sessions hosted by other peers to get virtual tutoring with other students
                  learning the same things as you, such as math, SAT Prep, AP Test Review, and more.
                </Text>
              </Box>
            </Center>
          </SimpleGrid>
          <SimpleGrid columns={[1, 2]} spacing="32px" margin="auto" justifyItems="center">
            <Center>
              <PublicImage width="240px" src="/peer-tutoring/icon2.png" />
            </Center>
            <Center>
              <Box>
                <Heading size="xl" as="h3" mb="16px">
                  Become a peer tutor
                </Heading>
                <Text>
                  As a student, you can give back by getting certified to host tutoring sessions
                  yourself! Prove your mastery and readiness to tutor topics through
                  Schoolhouse.world’s certification process, and volunteer your time for learners
                  who need it the most.
                </Text>
              </Box>
            </Center>
          </SimpleGrid>
          <Heading as="h2" size="2xl" mt="64px" mb="64px">
            Why join Schoolhouse.world as a{' '}
            <Box as="span" position="relative">
              learner
              <Box position="absolute" left="0px" bottom="-13px" zIndex={-1}>
                <WavyPath />
              </Box>
            </Box>
            ?
          </Heading>
          <Stack spacing="48px" align="center">
            <Stack direction={['column', 'row']} spacing="32px">
              <Center>
                <PublicImage width="180px" src="/peer-tutoring/icon4.png" />
              </Center>
              <Center>
                <Box>
                  <Heading size="xl" as="h3" mb="16px">
                    Learn things from a new angle
                  </Heading>
                  <Text>
                    When learning from peers, you can discover new approaches and techniques for
                    learning, and hear things explained in a different way than what you typically
                    see in your regular classroom.
                  </Text>
                </Box>
              </Center>
            </Stack>
            <Stack direction={['column', 'row']} spacing="32px">
              <Center>
                <PublicImage width="180px" src="/peer-tutoring/icon9.png" />
              </Center>
              <Center>
                <Box>
                  <Heading size="xl" as="h3" mb="16px">
                    Feel empowered, not intimidated
                  </Heading>
                  <Text>
                    In small-group sessions with tutors and learners who know what you’re going
                    through, you can build the confidence to ask questions and find your voice.
                  </Text>
                </Box>
              </Center>
            </Stack>
            <Stack direction={['column', 'row']} spacing="32px">
              <Center>
                <PublicImage width="180px" src="/peer-tutoring/icon3.png" />
              </Center>
              <Center>
                <Box>
                  <Heading size="xl" as="h3" mb="16px">
                    Build connections
                  </Heading>
                  <Text>
                    Join a group of other learners across the globe that have the same questions you
                    do, and find peer role models to help motivate you to achieve your goals.
                  </Text>
                </Box>
              </Center>
            </Stack>
            <Flex align="center">
              <AuthLink mode="signup" returnTo={getExploreUrl()}>
                <Button variant="solid" width={['100%', 'auto']}>
                  Get Started
                </Button>
              </AuthLink>
            </Flex>
          </Stack>
          <Heading as="h2" size="2xl" mt="64px" mb="64px">
            Why become a peer{' '}
            <Box as="span" position="relative">
              tutor
              <Box position="absolute" left="0px" bottom="-13px" zIndex={-1}>
                <WavyPath />
              </Box>
            </Box>{' '}
            on Schoolhouse.world?
          </Heading>
          <Stack align="center" spacing="48px">
            <Stack direction={['column', 'row']} spacing="32px">
              <Center>
                <PublicImage width="180px" src="/peer-tutoring/icon7.png" />
              </Center>
              <Center>
                <Box>
                  <Heading size="xl" as="h3" mb="16px">
                    Learn by teaching
                  </Heading>
                  <Text>
                    Studies have shown that teaching others is one of the best ways you can
                    reinforce and apply what you have learned. On the tutor side, peer to peer
                    tutoring benefits you by providing a space to grow academically.
                  </Text>
                </Box>
              </Center>
            </Stack>
            <Stack direction={['column', 'row']} spacing="32px">
              <Center>
                <PublicImage width="180px" src="/peer-tutoring/icon8.png" />
              </Center>
              <Center>
                <Box>
                  <Heading size="xl" as="h3" mb="16px">
                    Give back through volunteering
                  </Heading>
                  <Text>
                    Volunteer your time by hosting free sessions for other students who need help
                    the most. Plus, track the work you do as volunteering and service hours for
                    college applications and more!
                  </Text>
                </Box>
              </Center>
            </Stack>
            <Stack direction={['column', 'row']} spacing="32px">
              <Center>
                <PublicImage width="180px" src="/peer-tutoring/icon6.png" />
              </Center>
              <Center>
                <Box>
                  <Heading size="xl" as="h3" mb="16px">
                    Grow in a global community
                  </Heading>
                  <Text>
                    As a tutor, you can join an international community of other volunteers spanning
                    middle, high school, college, and beyond. Expand your horizons and challenge
                    your assumptions all while nurturing a shared mission of connecting the world
                    through learning.
                  </Text>
                </Box>
              </Center>
            </Stack>
            <Flex align="center">
              <ShwLink href={faq.getArticleUrl('become-a-tutor')}>
                <Button variant="solid" width={['100%', 'auto']}>
                  Become a Tutor
                </Button>
              </ShwLink>
            </Flex>
          </Stack>
          <FAQ items={FAQ_ITEMS} mt="64px" mb={4} category="tutoring" />
        </Box>
      </Container>
      <Box width="100%" bg="bg.info" py="32px">
        <Container centerContent>
          <Box maxWidth="750px" textAlign="center">
            <Heading size="3xl" as="h2" mb="16px" fontSize="32px">
              Join a community of learners and tutors today
            </Heading>
            <AuthLink mode="signup" returnTo={getExploreUrl()}>
              <Button variant="solid" width={['100%', 'auto']}>
                Get Started
              </Button>
            </AuthLink>
          </Box>
        </Container>
      </Box>
    </Layout>
  )
}

const PeerTutoringPage = makePage(PeerTutoringIndex, {
  onboardingUsers: 'allowed',
})
export const getServerSideProps = makeServerProps(PeerTutoringPage)
export default PeerTutoringPage
